import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TokenStorage } from './token.storage';
import { ReloginUser, User } from '../../models/user.model';
import { environment } from '../../../environments/environment';
import { APIResponse } from '../api/api.service';
import { CompanyService } from '../company/company.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    /**
     * Base API URL
     *
     * @private
     */
    private url = environment.apiBase;

    /**
     * Init
     *
     * @private
     */
    private userInitialized = false;

    /**
     * Logged user subject
     *
     * @private
     */
    private _userSubject = new ReplaySubject<User | null>(1);

    constructor(
        private http: HttpClient,
        private tokenStorage: TokenStorage,
        private companyService: CompanyService
    ) {
        // If token not exist, reset user, else wait for user$ async call (anywhere)
        if (!this.tokenStorage.getTokenForHeader()) {
            this.resetUser();
        }
    }

    /**
     * Logged user data
     */
    get user$(): Observable<User | null> {
        this.initUser();
        return this._userSubject.asObservable();
    }

    /**
     * Login user & store token with user id
     *
     * @param email
     * @param password
     * @param type
     */
    login(email: string, password: string, type: string): Observable<User> {
        return this.http.post<APIResponse<User>>(`${this.url}/login`, { email, password, type }).pipe(
            map((response) => response.data!),
            tap((data) => {
                console.log(data);
                this.setUser(data); // set user
                this.tokenStorage.saveToken(data.accessToken!, data.id);

                // get complete user
                firstValueFrom(this.me$()).then((user) => {
                    this.setUser(user!);
                });
                // TODO: store companies to localstorage
            })
        );
    }

    /**
     * Login user by token
     * @param reloginToken
     */
    loginByToken(reloginToken: string): Observable<ReloginUser> {
        return this.http.get<APIResponse<ReloginUser>>(`${this.url}/persons/relogin/${reloginToken}`).pipe(
            map((response) => response.data!),
            tap((data) => {
                this.setUser(data); // set user
                this.tokenStorage.saveToken(data.accessToken!, data.id);
                // get complete user
                firstValueFrom(this.me$()).then((user) => {
                    this.setUser(user!);
                });
            })
        );
    }

    /**
     *
     */
    getReloginToken(): Observable<string> {
        return this.http
            .get<APIResponse<{ reloginToken: string }>>(`${this.url}/persons/relogin`)
            .pipe(map((response) => response.data!.reloginToken));
    }

    /**
     * Generate one time password
     */
    generateOnetimePassword(): Promise<string> {
        return firstValueFrom(
            this.http
                .post<APIResponse<{ onetimePassword: string }>>(`${this.url}/onetime-password`, [])
                .pipe(map((response) => response.data!.onetimePassword))
        );
    }

    /**
     * Sign out user
     */
    signOut(): void {
        // TODO: call backend for sign out from backend
        this.tokenStorage.signOut();
        this.companyService.reset();
        this.resetUser();
    }

    /**
     * Get authorization headers as object
     */
    getAuthorizationHeaders() {
        return { Authorization: `Bearer ${this.getBearerToken()}` };
    }

    /**
     * Get Bearer Token (JWT)
     */
    getBearerToken(): string | null {
        return this.tokenStorage.getTokenForHeader() || '';
    }

    /**
     * Get actual logged user
     */
    user(): Promise<User | null> {
        return firstValueFrom(this.user$);
    }

    /**
     * Init user data from server observable
     *
     * @param force
     * @protected
     */
    protected initUser(force = false) {
        if (!this.userInitialized || force) {
            // get token
            const tokenData = this.tokenStorage.getTokenData();
            if (tokenData) {
                // get user data by token
                this.me$().subscribe((user) => {
                    if (user) {
                        this.setUser(user);
                        return;
                    }
                    this.resetUser();
                });
            }
        }
        this.userInitialized = true;
    }

    /**
     * Set user to observable
     *
     * @param user
     * @private
     */
    private setUser(user: User): void {
        if (user) {
            // TODO: create in backend first: user.isAdmin = user.roles.includes('admin');
        }

        this._userSubject.next(user);
    }

    /**
     * Reset user
     * @private
     */
    private resetUser(): void {
        this._userSubject.next(null);
    }

    /**
     * Get all data for logged user
     *
     * @private
     */
    private me$(): Observable<User | null> {
        return this.http
            .get<APIResponse<User>>(`${this.url}/persons/me?load[]=companies&companies.has-devices=true`)
            .pipe(
                map((response) => response.data!),
                catchError((data) => {
                    console.log(data);
                    return of(null);
                })
            );
    }

    updateIsMarketingFlag(isMarketing: boolean): Observable<User> {
        return this.http.put<APIResponse<User>>(`${this.url}/persons/me`, { isMarketing }).pipe(
            map((response) => response.data!),
            tap((data) => {
                this.setUser(data); // set user
            })
        );
    }
}
